import { gql } from "@apollo/client";

export const ASSETS_COUNT_QUERY = gql`
  query Assets {
    assets {
        total
    }
}
`;

export const ASSETS_QUERY = gql`
  query Assets($filter: AssetFilterInput, $limit: Int, $offset: Int) {
    assets(
        filter: $filter
        limit: $limit
        offset: $offset
        id: null
    ) {
        total
        assets {
            id
            name
            status
            coverage
            locationAddress
            maintenance
            upcomingInspection
            responsibleUsers {
                id
                firstName
                lastName
                email
            }
            locations {
                id
                locationName
            }
            product {
                id
                name
                category {
                    name
                    iconColor
                }
                type {
                    name
                }
            }
        }
    }
}`;

// Assets Query with Relations (for insights dashboard)

export const ASSETS_WITH_RELATIONS_QUERY = gql`
  query AssetsWithRelations($filter: AssetFilterInput, $limit: Int, $offset: Int) {
    assets(
        filter: $filter
        limit: $limit
        offset: $offset
        id: null
    ) {
        total
        assets {
          id
            name
            description
            productId
            serialNumber
            installationDate
            locationIds
            locationAddress
            status
            maintenance
            upcomingInspection
            assetRelations {
                id
                assetId
                fedFromId
                fedFromPartId
                fedFromAsset {
                    id
                    name
                    product {
                        typeName
                        type { name }
                    }
                }
            }
            product {
                id
                name
                category {
                    id
                    name
                }
                type {
                    id
                    name
                }
            }
        }
    }
}`;

// Eligible fed-from assets (product-type knowledge + location scope)

export const ELIGIBLE_FED_FROM_ASSETS_QUERY = gql`
  query EligibleFedFromAssets($input: EligibleFedFromAssetsInput!) {
    eligibleFedFromAssets(input: $input) {
      total
      filterReason
      fedFromAnswer
      resolvedProductTypeId
      resolvedRootLocationId
      inputLocationId
      matchedProductTypes {
        id
        name
        groupType
        confidence
        matchReason
      }
      assets {
        id
        name
        locations {
          id
          locationName
        }
        product {
          id
          name
          typeName
          type {
            id
            name
          }
        }
      }
    }
  }
`;

// Simple Assets Query (for dropdowns - id and name only)

export const SIMPLE_ASSETS_QUERY = gql`
  query SimpleAssets($limit: Int, $offset: Int) {
    assets(limit: $limit, offset: $offset) {
      total
      assets {
        id
        name
        aliasNames
        locations {
          id
          locationName
        }
        product {
          type {
            name
          }
        }
      }
    }
  }
`;

// Get Single Asset by ID Query

export const GET_ASSET_QUERY = gql`
  query Asset($id: ID!) {
    asset(id: $id) {
        id
        name
        description
        productId
        serialNumber
        installationDate
        fileIds
        status
        monitored
        locationAddress
        maintenanceIds
        coverage
        position
        specifications
        createdAt
        updatedAt
        deletedAt
        lastServiceDate
        lastInspectionDate
        maintenance
        obligations
        children {
            id
            name
            description
            productId
            serialNumber
            maintenance
             product {
                id
                categoryId
                categoryName
                typeId
                typeName
                name
            }
            maintenanceSchedules {
                id
                assetId
                title
                description
                maintenanceTasks
            }
            upcomingInspection
        }
        locations {
            id
            locationName
            description
            locationType
            locationTypeName
            address
            city
            state
            zipcode
            country
            coordinates
            parentId
            fileIds
        }
        responsibleUsers {
            id
            email
            firstName
            lastName
          
        }
        workOrders {
            id
            title
            severity
            workOrderType
            workOrderServiceCategory
            workOrderStageId
            startDate
            endDate
            executionPriority
            workOrderStage {
                name
                color_code
                id
            }
        }
        product {
            id
            parentId
            categoryId
            categoryName
            typeId
            typeName
            manufacturerId
            manufacturerName
            successorId
            predecessorId
            category {
                id
                name
                iconName
                iconColor
                iconType
            }
            type {
                id
                name
                iconName
                iconColor
                iconType
                fieldDefinitions
            }
            name
            series
            make
            model
            serialNumber
            dataSheets
            lifespan
            rating
            specifications
            images
            description
            lifecycleStatus
            manufacturerStatus
            isActive
            createdAt
            updatedAt
            deletedAt
            isAddedAsset
        }
        assetRelations {
            id
            assetId
            fedFromId
            fedFromPartId
            createdAt
            fedFromAsset {
                id
                name
                productId
                product {
                    typeId
                    typeName
                }
                locationAddress
                locations {
                    locationName
                }
            }
        }
             inspectionDetails {
            id
            inspectionOrderId
            inspectionStatus
            userNotes
            completedByUser {
                firstName
                lastName
            }
            approvedByUser {
                firstName
                lastName
            }
            inspectionOrder {
                id
                name
                assets {
                    attachments
                    inspectionStatus
                    completedAt
                    createdAt
                }
                dueDate
                status
            }
            approverNotes
        }
        assetMaintenanceSchedules {
            id
            assetId
            title
            description
            startDate
            nextDueDate
            assignedToUserIds
            createdAt
            updatedAt
            deletedAt
            scheduleType
            intervalUnit
            intervalValue
            timeZone
        }
        breakerManagement {
          panelConfig {
            layoutMode
            numberOfBreakers
            breakerStartNumber
            mainBreaker {
              enabled
              position
              poles
            }
          }
          breakers {
            relationId
            assetIds
            breakerName
            poles
            amps
            phase
            breakerType
            description
            slot
            slotSpan
            columnIndex
            displayOrder
            metadata
            affectedAreas {
              id
              locationName
              locationAddress
            }
          }
        }
        metadata
      upcomingInspection
    }
  }
`;

// Maintenance Schedule Query
// Get Files by Belongs To Query

export const ASSET_MAINTENANCE_SCHEDULES_QUERY = gql`
    query AssetMaintenanceSchedules($assetId: ID!) {
        assetMaintenanceSchedules(assetId: $assetId) {
            id
            assetId
            title
            description
            scheduleType
            intervalUnit
            intervalValue
            startDate
            nextDueDate
            timeZone
            assignedToUserIds
            createdAt
            updatedAt
            deletedAt
        }
    }
`;
// Get Company AI Addons Query
// Get Asset Maintenance Activities Query

export const ASSET_TIMELINE_QUERY = gql`
  query AssetTimeline($assetId: ID!) {
    assetTimeline(assetId: $assetId) {
      id
      assetId
      type
      title
      notes
      evidenceFileIds
      metadata
      createdBy
      createdAt
      updatedAt
      files {
        id
        file_name
        file_url
        mime_type
        file_size
        upload_status
        created_at
      }
      createdByUser {
        id
        firstName
        lastName
        email
        role
      }
    }
  }
`;

// Location Timeline / Lifecycle Activity Feed — same shape as assetTimeline
// (TimelineEvent fields), with `locationId` instead of `assetId`. Consumed by
// the shared EntityTimelineCard so the two surfaces stay in sync.
